import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, DeepMap, FieldError, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { field } from '../../ui/panel';
import styled from 'styled-components';
import { FlexContainer } from '../../ui/Container';
import { DatePickerGlobalStyles, renderDatePickerPopper } from '../../ui/Date/calendarPopperStyles';
import { Error, Label } from '../../ui/InputText/style';
import { DefaultLayoutPadding } from '../../../constants/Layout';
import theme from '../../../theme';

type ReactDatePickerFormInputProps<T> = UseControllerProps<T & FieldValues> & {
  name: FieldPath<T & FieldValues>;
  errors?: Partial<DeepMap<T & FieldValues, FieldError>>;
  placeholder?: string;
  label?: string;
  /**
   * Expected output format:
   * - 'date-object': Form field expects Date objects (default)
   * - 'date-string': Form field expects YYYY-MM-DD strings
   */
  outputFormat?: 'date-object' | 'date-string';
  disabled?: boolean;
  /**
   * Maximum selectable date.
   * - If not provided, defaults to today (for date of birth scenarios)
   * - If set to null, no maximum date restriction (allows future dates for expiry dates)
   * - If set to a Date, uses that specific date as maximum
   */
  maxDate?: Date | null;
};

// Styled DatePicker component that matches the existing InputText styling exactly
const StyledDatePicker = styled(DatePicker)<{ error?: string; disabled?: boolean }>`
  ${field};
  height: 44px;
  width: 100%;
  ${(p) => p.error && `border-color: ${p.theme.colors.general.error};`}
  color: ${(p) => (p.disabled ? p.theme.colors.general.textSecondary : p.theme.colors.general.black)};
  font-size: ${(p) => p.theme.font.size.sm};
  padding: 0 12px;
  background: ${(p) => (p.disabled ? p.theme.colors.grey[5] : p.theme.colors.general.white)};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};

  @media (max-width: ${(p) => p.theme.screen.xs}) {
    font-size: ${(p) => p.theme.font.size.xs};
  }

  &::placeholder {
    color: ${(p) => p.theme.colors.general.placeholder};
  }
`;

/**
 * Convert any date value into a **local-midnight** Date for react-datepicker.
 *
 * react-datepicker always renders using the browser's local timezone, so the
 * Date we hand it must have the desired calendar day in **local** parts.
 *
 * Inputs may be:
 *   - Date objects with UTC midnight (from API / zod coercion)
 *   - YYYY-MM-DD strings (from date-string output format)
 *   - Full ISO strings (from legacy data)
 */
const formatForDatePicker = (val: any): Date | null => {
  if (!val) return null;

  if (val instanceof Date && val.getTime() === 0) return null;
  if (typeof val === 'string' && val.startsWith('1970-01-01')) return null;

  try {
    if (val instanceof Date) {
      // Date objects from our pipeline represent UTC midnight.
      // Extract UTC Y/M/D and build a local-midnight Date so the picker shows the right day.
      return new Date(val.getUTCFullYear(), val.getUTCMonth(), val.getUTCDate());
    }

    if (typeof val === 'string') {
      // Extract Y-M-D from the leading portion of the string and build local midnight.
      const ymdMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (ymdMatch) {
        return new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
      }
      // Fallback: try native parsing, then extract UTC calendar day.
      const date = new Date(val);
      if (!isNaN(date.getTime())) {
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
      }
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Convert the Date emitted by react-datepicker into the value stored in the form.
 *
 * react-datepicker always returns a Date at **local midnight** for the selected
 * calendar day, so we extract **local** parts (getFullYear/getMonth/getDate).
 */
const convertDatePickerValue = (date: Date | null, outputFormat: 'date-object' | 'date-string'): any => {
  if (!date) return outputFormat === 'date-object' ? null : '';

  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  if (outputFormat === 'date-string') {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  // Build a UTC-midnight Date so that SmartDateToString / API serialization
  // always reads the same calendar day regardless of browser timezone.
  return new Date(Date.UTC(year, month, day));
};

const ReactDatePickerFormInput = <T extends FieldValues>({
  control,
  name,
  errors = {},
  placeholder,
  label,
  outputFormat = 'date-object',
  disabled = false,
  maxDate,
  ...other
}: ReactDatePickerFormInputProps<T>) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const errorMessage = errors?.[name]?.message;

  // Determine maxDate: if explicitly null, don't restrict (allows future dates)
  // If undefined, default to today (backward compatibility for date of birth)
  // If provided as Date, use that specific date
  const effectiveMaxDate = maxDate === null ? undefined : maxDate ?? new Date();

  return (
    <DatePickerGlobalStyles>
      <FlexContainer style={{ position: 'relative', width: '100%' }} flexDirection="column">
        {label && <Label htmlFor={name}>{label}</Label>}
        <Error
          style={{
            position: 'absolute',
            top: label ? '20px' : '-20px',
            paddingLeft: theme.spacing[DefaultLayoutPadding],
          }}
        >
          {errorMessage}
        </Error>
        <Controller
          name={name}
          control={control}
          render={({ field: { value, onChange } }) => (
            <StyledDatePicker
              selected={formatForDatePicker(value)}
              onChange={(date: Date | null) => {
                const convertedValue = convertDatePickerValue(date, outputFormat);
                onChange(convertedValue);
              }}
              placeholderText={placeholder}
              dateFormat="dd/MM/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              minDate={new Date('1900-01-01')}
              {...(effectiveMaxDate !== undefined && { maxDate: effectiveMaxDate })}
              disabled={disabled}
              error={errorMessage}
              autoComplete="off"
              // Enable clearing functionality
              isClearable={true}
              // Ensure consistent styling
              wrapperClassName="date-picker-wrapper"
              calendarClassName="date-picker-calendar"
              popperContainer={renderDatePickerPopper}
              popperProps={{ strategy: 'fixed' }}
              {...other}
            />
          )}
        />
      </FlexContainer>
    </DatePickerGlobalStyles>
  );
};

export default ReactDatePickerFormInput;
